import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  TablePagination,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  BeachAccess,
  Flight,
  Description,
  AllInclusive,
} from "@material-ui/icons";
import FileValidationRequestService from "../../../services/FileValidationRequestService";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import MainContainer from '../../../components/MainContainer';
import { headerData } from '../../../data/modules/my-validations-header';

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
      paddingBottom: theme.spacing(12),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      paddingBottom: theme.spacing(10),
    },
  },
  title: {
    fontWeight: "bold",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.1rem',
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
    },
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
    },
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: `2px solid transparent`,
    minWidth: 180,
    flex: "1 0 auto",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 'calc(25% - 4px)',
      flex: '1 1 calc(25% - 4px)',
      padding: theme.spacing(0.75, 0.5),
      gap: theme.spacing(0.5),
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      borderRadius: theme.spacing(0.5),
      border: `1px solid ${theme.palette.divider}`,
    },
    [theme.breakpoints.down(400)]: {
      minWidth: 'calc(33.33% - 4px)',
      flex: '1 1 calc(33.33% - 4px)',
      padding: theme.spacing(0.5, 0.25),
    },
  },
  statCardActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    boxShadow: theme.shadows[4],
    transform: "translateY(-4px)",
    [theme.breakpoints.down('xs')]: {
      transform: "translateY(-2px)",
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  statCardIcon: {
    fontSize: 32,
    color: theme.palette.primary.main,
    opacity: 0.7,
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
    },
  },
  statCardCount: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
    },
  },
  statCardLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.6rem',
    },
    [theme.breakpoints.down(400)]: {
      fontSize: '0.55rem',
    },
  },
  tableWrapper: {
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    [theme.breakpoints.down('xs')]: {
      borderRadius: theme.spacing(0.5),
    },
  },
  tableContainer: {
    maxHeight: "55vh",
    [theme.breakpoints.down('xs')]: {
      maxHeight: "45vh",
    },
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.7rem',
      padding: '8px 4px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  tableCell: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.7rem',
      padding: '6px 4px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  chipConge: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.55rem',
      height: '18px',
    },
  },
  chipMission: {
    backgroundColor: '#b9cef1',
    color: theme.palette.primary.dark,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.55rem',
      height: '18px',
    },
  },
  chipRapport: {
    backgroundColor: '#fff3e0',
    color: theme.palette.secondary.dark,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.55rem',
      height: '18px',
    },
  },
  noData: {
    padding: theme.spacing(4),
    textAlign: "center",
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  footerInfo: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.65rem',
      marginTop: theme.spacing(1),
      paddingBottom: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  resetLink: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    textDecoration: "underline",
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('xs')]: {
      '& .MuiTablePagination-toolbar': {
        flexWrap: 'wrap',
        justifyContent: 'center',
        padding: theme.spacing(0.5),
        minHeight: '36px',
      },
      '& .MuiTablePagination-select': {
        fontSize: '0.65rem',
        padding: '2px 20px 2px 8px',
      },
      '& .MuiTablePagination-selectLabel': {
        fontSize: '0.65rem',
        margin: 0,
      },
      '& .MuiTablePagination-displayedRows': {
        fontSize: '0.65rem',
        margin: 0,
      },
      '& .MuiTablePagination-actions': {
        marginLeft: '4px',
      },
    },
  },
  // Styles pour la version mobile en cartes
  mobileCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.25, 0),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  mobileCardLabel: {
    fontWeight: 'bold',
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    minWidth: '70px',
    [theme.breakpoints.down(400)]: {
      minWidth: '55px',
      fontSize: '0.6rem',
    },
  },
  mobileCardValue: {
    fontSize: '0.7rem',
    textAlign: 'right',
    wordBreak: 'break-word',
    flex: 1,
    [theme.breakpoints.down(400)]: {
      fontSize: '0.65rem',
    },
  },
  mobileCardChip: {
    fontSize: '0.55rem',
    height: '18px',
  },
  mobileCardContainer: {
    maxHeight: '45vh',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(0.5),
  },
  mobilePaper: {
    borderRadius: theme.spacing(0.5),
  },
  // Styles pour le scroll
  scrollRoot: {
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    flex: 1,
    height: '100%',
    [theme.breakpoints.down('xs')]: {
      height: '100%',
      maxHeight: '100%',
      overflow: 'auto',
    },
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    [theme.breakpoints.down('xs')]: {
      minHeight: '100%',
    },
  },
  paginationWrapper: {
    marginTop: 'auto',
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(4),
    },
  },
}));

// Normalisation des types
const normalizeType = (raw) => {
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower.includes("congé") || lower.includes("conge")) return "conge";
  if (lower.includes("rapport")) return "rapport de mission";
  if (lower.includes("mission")) return "mission";
  return "";
};

// Définition des types de demandes
const REQUEST_TYPES = [
  {
    key: "conge",
    label: "Congé",
    icon: BeachAccess,
    match: (raw) => normalizeType(raw) === "conge",
  },
  {
    key: "mission",
    label: "Mission",
    icon: Flight,
    match: (raw) => normalizeType(raw) === "mission",
  },
  {
    key: "rapport de mission",
    label: "Rapport de mission",
    icon: Description,
    match: (raw) => normalizeType(raw) === "rapport de mission",
  },
];

// Statistiques affichées (avec "Toutes" en plus)
const STATS_DISPLAY = [
  {
    key: "all",
    label: "Toutes",
    icon: AllInclusive,
  },
  ...REQUEST_TYPES,
];

export default function MyPendingDemands() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef(null);

  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  
  // État pour la pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 3 : 7);

  useEffect(() => {
    // Ajuster rowsPerPage sur mobile
    if (isMobile) {
      setRowsPerPage(3);
    } else {
      setRowsPerPage(7);
    }
  }, [isMobile]);

  useEffect(() => {
    setOpenLoadingSimpleBackdrop(true);
    FileValidationRequestService.getMyPendingDemands()
      .then((result) => {
        if (result && result.data) {
          const formattedRows = result.data.map((row) => ({
            ...row,
            id: row.file_validation_request_id,
          }));
          setRows(formattedRows);
          setFilteredRows(formattedRows);
          setOpenLoadingSimpleBackdrop(false);
        } else {
          setOpenLoadingSimpleBackdrop(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setOpenLoadingSimpleBackdrop(false);
      });
  }, []);

  // Obtenir le count pour un type spécifique
  const getCountForType = (typeDef) => {
    if (typeDef.key === "all") return rows.length;
    return rows.filter((row) => typeDef.match(row.libelle_module_type)).length;
  };

  // Gérer le clic sur une statistique
  const handleStatClick = (typeKey) => (event) => {
    // Si on clique sur le même type, on désactive le filtre
    if (typeFilter === typeKey) {
      setTypeFilter("all");
      setFilteredRows(rows);
    } else {
      setTypeFilter(typeKey);
      // Filtrer par type
      if (typeKey === "all") {
        setFilteredRows(rows);
      } else {
        const typeDef = REQUEST_TYPES.find((t) => t.key === typeKey);
        if (typeDef) {
          const filtered = rows.filter((row) => typeDef.match(row.libelle_module_type));
          setFilteredRows(filtered);
        }
      }
    }
    // Réinitialiser la page lors du changement de filtre
    setPage(0);
    // Scroll en haut
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // Réinitialiser tous les filtres
  const handleReset = () => {
    setTypeFilter("all");
    setFilteredRows(rows);
    setPage(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // Gestionnaire de changement de page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Scroll en haut du contenu
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // Gestionnaire de changement du nombre de lignes par page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // Colonnes du tableau
  const columns = [
    { field: "submission_time", headerName: "Date de la demande", width: 150 },
    { field: "submitter_person_first_name", headerName: "Demandeur", width: 200 },
    { field: "reference", headerName: "Référence", width: 120 },
    { field: "libelle_module_type", headerName: "Type", width: 150 },
    { field: "debut_date", headerName: "Date de départ", width: 130 },
    { field: "fin_date", headerName: "Date de retour", width: 130 },
    {
      field: "validateur",
      headerName: "Validateur en cours",
      width: 200,
      valueFormatter: (params) => {
        return `${params.row.last_name_validateur || ''} ${params.row.first_name_validateur || ''}`;
      },
    },
  ];

  // Obtenir le chip de type avec la bonne couleur
  const getTypeChip = (type) => {
    const normalized = normalizeType(type);
    let className = classes.chipConge;
    let label = type || "Non défini";

    if (normalized === "conge") {
      className = classes.chipConge;
      label = "Congé";
    } else if (normalized === "mission") {
      className = classes.chipMission;
      label = "Mission";
    } else if (normalized === "rapport de mission") {
      className = classes.chipRapport;
      label = "Rapport de mission";
    }

    return (
      <Chip
        label={label}
        className={className}
        size="small"
      />
    );
  };

  // Rendu de la carte mobile
  const MobileCard = ({ row }) => {
    return (
      <Box className={classes.mobileCard}>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Demandeur</Typography>
          <Typography className={classes.mobileCardValue}>
            {row.demandeur || "-"}
          </Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Date demande</Typography>
          <Typography className={classes.mobileCardValue}>
            {row.submission_time || "-"}
          </Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Référence</Typography>
          <Typography className={classes.mobileCardValue}>
            {row.reference || "-"}
          </Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Type</Typography>
          <Box>
            {row.libelle_module_type ? getTypeChip(row.libelle_module_type) : "-"}
          </Box>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Départ</Typography>
          <Typography className={classes.mobileCardValue}>
            {row.debut_date || "-"}
          </Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Retour</Typography>
          <Typography className={classes.mobileCardValue}>
            {row.fin_date || "-"}
          </Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Validateur</Typography>
          <Typography className={classes.mobileCardValue}>
            {row.last_name_validateur && row.first_name_validateur
              ? `${row.last_name_validateur} ${row.first_name_validateur}`
              : "-"}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Rendu du tableau ou des cartes
  const renderContent = () => {
    if (filteredRows.length === 0) {
      return (
        <Paper className={classes.noData} elevation={0}>
          <Typography variant="body1" color="textSecondary">
            {typeFilter !== "all" 
              ? "Aucune demande ne correspond au type sélectionné" 
              : "Aucune demande en attente"}
          </Typography>
        </Paper>
      );
    }

    // Calculer les lignes à afficher pour la page courante
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentRows = filteredRows.slice(startIndex, endIndex);

    // Version mobile - Cartes
    if (isMobile) {
      return (
        <Paper className={classes.mobilePaper} elevation={1}>
          <Box className={classes.mobileCardContainer}>
            {currentRows.map((row) => (
              <MobileCard key={row.id} row={row} />
            ))}
          </Box>
        </Paper>
      );
    }

    // Version desktop - Tableau
    return (
      <Paper className={classes.tableWrapper} elevation={1}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    className={classes.tableHeaderCell}
                    style={{ minWidth: col.width }}
                  >
                    {col.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell className={classes.tableCell}>{row.submission_time || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{row.demandeur || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{row.reference || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {row.libelle_module_type ? getTypeChip(row.libelle_module_type) : "-"}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{row.debut_date || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{row.fin_date || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {row.last_name_validateur && row.first_name_validateur
                      ? `${row.last_name_validateur} ${row.first_name_validateur}`
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <MainContainer {...headerData}>
      <Box className={classes.scrollRoot} ref={scrollRef}>
        <Box className={classes.contentWrapper}>
          <Box className={classes.root}>
            <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />

            <Typography variant="h5" className={classes.title}>
              Mes demandes en attente
            </Typography>

            {/* Statistiques cliquables par type */}
            <Box className={classes.statsContainer}>
              {STATS_DISPLAY.map((typeDef) => {
                const Icon = typeDef.icon;
                const isActive = typeFilter === typeDef.key;
                const count = getCountForType(typeDef);
                const labelShort = isMobile && typeDef.label.length > 6 
                  ? typeDef.label.substring(0, 6) 
                  : typeDef.label;
                return (
                  <Box
                    key={typeDef.key}
                    className={`${classes.statCard} ${isActive ? classes.statCardActive : ''}`}
                    onClick={handleStatClick(typeDef.key)}
                    role="button"
                    tabIndex={0}
                    title={`Filtrer sur : ${typeDef.label}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleStatClick(typeDef.key)(e);
                      }
                    }}
                  >
                    <Icon className={classes.statCardIcon} />
                    <Box>
                      <Typography className={classes.statCardCount}>
                        {count}
                      </Typography>
                      <Typography className={classes.statCardLabel}>
                        {labelShort}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Tableau ou Cartes selon le device */}
            {renderContent()}

            {/* Pagination */}
            <Box className={classes.paginationWrapper}>
              <TablePagination
                className={classes.pagination}
                rowsPerPageOptions={isMobile ? [3, 5, 10] : [5, 7, 10, 25]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) => 
                  isMobile ? `${from}-${to}/${count}` : `${from}-${to} sur ${count}`
                }
                labelRowsPerPage={isMobile ? "" : "Lignes par page :"}
              />

              {/* Informations de footer */}
              <Typography className={classes.footerInfo}>
                {filteredRows.length} demande(s) affichée(s) sur {rows.length} au total
                {typeFilter !== "all" && (
                  <>
                    {" • "}
                    <strong>
                      Filtré par : {STATS_DISPLAY.find(s => s.key === typeFilter)?.label}
                    </strong>
                    {" • "}
                    <span
                      className={classes.resetLink}
                      onClick={handleReset}
                    >
                      Réinitialiser
                    </span>
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </MainContainer>
  );
}